import { useMemo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { streamEditorPath } from 'Lessons';
import { SectionHeading, SelectedChipList } from 'ContentItemEditor';
import { courseTitleWithTag } from './useEditorCourses';
import { type EditorCourse } from './StreamEditor.types';

// Roughly four pills. Past that the list scrolls rather than pushing the card open.
const MAX_LIST_HEIGHT = 160;

const ICON_SIZE = 16;

const TOOLTIP =
    'Courses that share the same requirement identifier as the current course. Both published and unpublished courses are shown.';

type Props = {
    requirementIdentifier: string;
    streamId: string;
    localePackId: string | null | undefined;
    courses: EditorCourse[];
    loading: boolean;
};

/*
    The other courses that share this course's requirement identifier. Scores and completion carry
    across them when a student defers into a new cohort, so an admin needs to see who else is in the
    set before changing the identifier.
*/
export default function EquivalentCoursesList({
    requirementIdentifier,
    streamId,
    localePackId,
    courses,
    loading,
}: Props) {
    const equivalentCourses = useMemo(() => {
        if (!requirementIdentifier) return [];

        return courses.filter(
            course =>
                course.requirementIdentifier === requirementIdentifier &&
                course.id !== streamId &&
                // A translation of this course is the same course, so it is not an equivalent one.
                (!localePackId || course.localePackId !== localePackId),
        );
    }, [courses, requirementIdentifier, streamId, localePackId]);

    if (!requirementIdentifier) return null;

    return (
        <Stack spacing={1}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <SectionHeading>Equivalent Courses</SectionHeading>
                <Tooltip title={TOOLTIP}>
                    {/* One step above the 14px heading. MUI's own fontSize="small" is 20px, which
                        against a 12px cap height read as a separate element parked beside the
                        heading; at the text size itself the glyph was too small to read. */}
                    <InfoOutlined sx={{ fontSize: ICON_SIZE }} />
                </Tooltip>
            </Stack>
            {loading && (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <FrontRoyalSpinner className="inline" />
                    <Typography variant="body2">Loading equivalent courses…</Typography>
                </Stack>
            )}
            {!loading && equivalentCourses.length === 0 && (
                <Typography variant="body2">No other courses found with this requirement identifier.</Typography>
            )}
            {/* Capped and scrollable: every cohort version of a core course shares one
                identifier, so 'strategy_exam' covers 18 courses today. */}
            {!loading && equivalentCourses.length > 0 && (
                <Box sx={{ maxHeight: MAX_LIST_HEIGHT, overflowY: 'auto' }}>
                    <SelectedChipList
                        items={equivalentCourses.map(course => ({
                            key: course.id,
                            label: course.wasPublished
                                ? courseTitleWithTag(course)
                                : `${courseTitleWithTag(course)} (not published)`,
                            href: streamEditorPath({ streamId: course.id }),

                            // An unpublished equivalent is the one an admin needs to notice, so it
                            // keeps the red the Angular page gave it.
                            color: course.wasPublished ? undefined : 'error',
                        }))}
                    />
                </Box>
            )}
        </Stack>
    );
}
